/**
 * Ground marks in the FX pixel rules (docs/design/ui/2026-10-11-ground-aim/README.md, option A): a circle on the ground
 * drawn as 1-px dots on the scene grid (sampled in world space, so it is right at any camera yaw), a 50 % checker fill
 * in a ramp tone (a cached picture locked to the art grid, never an alpha blend), a 3×3 centre pip with a dark rim, and the
 * art's tint for a move. Shared by the aim reticle (passes/groundAim.ts) and the landing marks (passes/groundLanding.ts).
 */
import { getSkill, type World } from '@nine/shared';
import type { IsoCamera } from './camera';
import { FX_FPS, FX_RAMPS } from './fx/pixelRules';
import { skillLook, type FxElement } from './skillFx';

type G = CanvasRenderingContext2D;

/** A tone of an FX ramp, the index clamped. */
export const tone = (ramp: string, i: number): string => {
  const r = FX_RAMPS[ramp] ?? FX_RAMPS.azure!;
  return r[Math.max(0, Math.min(r.length - 1, i))]!;
};

const RAMP_OF: Record<FxElement, string> = {
  metal: 'steel',
  fire: 'fire',
  poison: 'leaf',
  earth: 'ochre',
  water: 'azure',
  wind: 'jade',
  thunder: 'violet',
  yin: 'violet',
  yang: 'gold',
  blood: 'maroon',
  ink: 'steel',
  heal: 'jade',
};
const rampCache = new Map<string, string>();
/** The ramp a move's ground marks are tinted with (its effect's element). */
export function rampOfSkill(skillId: string): string {
  let r = rampCache.get(skillId);
  if (!r) {
    let el: FxElement = 'water';
    try {
      el = skillLook(getSkill(skillId), 'jian').element;
    } catch {
      // an unknown move: the default tint
    }
    r = RAMP_OF[el] ?? 'azure';
    rampCache.set(skillId, r);
  }
  return r;
}

/** A time held to the 12 fps effect clock. */
export const stepped = (t: number): number => Math.floor(t * FX_FPS) / FX_FPS;

export interface GroundPen {
  g: G;
  iso: IsoCamera;
  ox: number;
  oy: number;
}

/** A ground point in the world picture, on whole art pixels. */
const at = (p: GroundPen, x: number, y: number): [number, number] => {
  const [sx, sy] = p.iso.proj(x, y);
  return [Math.round(sx + p.ox), Math.round(sy + p.oy)];
};

/**
 * A circle of 1-px dots round (x, y) (tiles), radius r: every dot, or `on` in each `every` (a dotted or dashed ring),
 * shifted by `phase`.
 */
export function dotRing(p: GroundPen, x: number, y: number, r: number, css: string, every = 1, phase = 0, on = 1): void {
  const n = Math.max(32, Math.round(r * 64));
  p.g.fillStyle = css;
  let lx = NaN;
  let ly = NaN;
  for (let i = 0; i < n; i++) {
    if (every > 1 && (i + phase) % every >= on) continue;
    const a = (i / n) * Math.PI * 2;
    const [sx, sy] = at(p, x + Math.cos(a) * r, y + Math.sin(a) * r);
    if (sx === lx && sy === ly) continue;
    lx = sx;
    ly = sy;
    p.g.fillRect(sx, sy, 1, 1);
  }
}

/** The screen half-axes of a ground circle of radius r (whole art pixels; a circle reads the same at any yaw). */
function axes(p: GroundPen, x: number, y: number, r: number): [number, number] {
  const [cx, cy] = p.iso.proj(x, y);
  let rx = 0;
  let ry = 0;
  for (let i = 0; i < 16; i++) {
    const a = (i / 16) * Math.PI * 2;
    const [sx, sy] = p.iso.proj(x + Math.cos(a) * r, y + Math.sin(a) * r);
    rx = Math.max(rx, Math.abs(sx - cx));
    ry = Math.max(ry, Math.abs(sy - cy));
  }
  return [Math.round(rx), Math.round(ry)];
}

/** Checker discs and bands, drawn once per size, band step, colour and grid parity (the 96 last used kept). */
const discs = new Map<string, HTMLCanvasElement>();
function disc(rx: number, ry: number, band: number, css: string, parity: number): HTMLCanvasElement {
  const key = `${rx},${ry},${band},${css},${parity}`;
  let c = discs.get(key);
  if (c) {
    discs.delete(key);
    discs.set(key, c);
    return c;
  }
  c = document.createElement('canvas');
  c.width = rx * 2 + 1;
  c.height = ry * 2 + 1;
  const cg = c.getContext('2d')!;
  const img = cg.createImageData(c.width, c.height);
  const col = [1, 3, 5].map((i) => parseInt(css.slice(i, i + 2), 16));
  const inner = 1 - band / 8;
  for (let j = 0; j < c.height; j++)
    for (let i = 0; i < c.width; i++) {
      if ((i + j + parity) & 1) continue;
      const d = Math.hypot((i - rx) / rx, (j - ry) / ry);
      if (d > 1 || (band < 8 && d < inner)) continue;
      const k = (j * c.width + i) * 4;
      img.data[k] = col[0]!;
      img.data[k + 1] = col[1]!;
      img.data[k + 2] = col[2]!;
      img.data[k + 3] = 255;
    }
  cg.putImageData(img, 0, 0);
  discs.set(key, c);
  if (discs.size > 96) discs.delete(discs.keys().next().value!);
  return c;
}

/**
 * A 50 % checker fill of a ground circle in `css` (a cached picture, locked to the art grid); `band` < 8 fills only the
 * outer band/8 of the radius (a fill closing in from the edge, in 8 steps).
 */
export function checkerFill(p: GroundPen, x: number, y: number, r: number, css: string, band = 8): void {
  if (r <= 0 || band <= 0) return;
  const [rx, ry] = axes(p, x, y, r);
  if (rx < 1 || ry < 1) return;
  const [cx, cy] = at(p, x, y);
  const left = cx - rx;
  const top = cy - ry;
  const c = disc(rx, ry, Math.min(8, Math.round(band)), css, (left + top) & 1);
  const g = p.g;
  const smooth = g.imageSmoothingEnabled;
  g.imageSmoothingEnabled = false;
  g.drawImage(c, left, top);
  g.imageSmoothingEnabled = smooth;
}

/** A 3×3 centre pip with a 1-px dark rim. */
export function pip(p: GroundPen, x: number, y: number, css: string, dark: string): void {
  const [sx, sy] = at(p, x, y);
  p.g.fillStyle = dark;
  p.g.fillRect(sx - 2, sy - 2, 5, 5);
  p.g.fillStyle = css;
  p.g.fillRect(sx - 1, sy - 1, 3, 3);
}

/** Small tick marks outside a ground circle, `lit` of 8 lit (a landing countdown), starting at the top of the screen. */
export function countdownTicks(p: GroundPen, x: number, y: number, r: number, lit: number, css: string): void {
  // Start at the circle's point nearest the screen's top and go round clockwise on screen.
  let best = 0;
  let bestY = Infinity;
  for (let s = 0; s < 8; s++) {
    const b = (s / 8) * Math.PI * 2;
    const sy = p.iso.proj(x + Math.cos(b), y + Math.sin(b))[1];
    if (sy < bestY) {
      bestY = sy;
      best = b;
    }
  }
  p.g.fillStyle = css;
  for (let q = 0; q < Math.min(8, lit); q++) {
    const a = (q / 8) * Math.PI * 2;
    const [sx, sy] = at(p, x + Math.cos(best + a) * (r + 0.3), y + Math.sin(best + a) * (r + 0.3));
    p.g.fillRect(sx - 1, sy, 3, 1);
    p.g.fillRect(sx, sy - 1, 1, 3);
  }
}

/**
 * The full landing mark of a ground move at (x, y): the art ring, the checker closing in from the edge as `k` (0..1) runs,
 * and on the foe's side (anyone but the caster) a red 1-px edge outside and a countdown of 8 ticks.
 */
export function landingMark(p: GroundPen, x: number, y: number, r: number, ramp: string, k: number, foeSide: boolean): void {
  const kk = Math.max(0, Math.min(1, k));
  checkerFill(p, x, y, r, tone(ramp, 3), Math.max(1, Math.ceil(kk * 8)));
  dotRing(p, x, y, r + 0.05, tone(ramp, 0));
  dotRing(p, x, y, r, tone(ramp, 5));
  if (foeSide) {
    dotRing(p, x, y, r + 0.11, tone('maroon', 4));
    countdownTicks(p, x, y, r, Math.ceil(kk * 8), tone('maroon', 5));
  }
}

/** Whether `e` is someone the player should read as a foe's caster (anyone but the player). */
export const foeSideOf = (w: World, srcId: number): boolean => w.player.id !== srcId;

/** A ground move that has just landed: its spot and blast, kept briefly so the area is seen (passes/groundLanding.ts). */
export interface LandStamp {
  src: number;
  skillId: string;
  x: number;
  y: number;
  r: number;
  /** World time it landed. */
  t: number;
}
/** Landed ground moves, newest last (fed by fx/groundLand.ts from the `skill` events with a ground point). */
export const landStamps: LandStamp[] = [];
/** How long a landed area stays on the ground (seconds). */
export const STAMP_LIFE = 0.5;
